import { BROWSER_TYPES, MOBILE } from '@intigral/constant/common';

/**
 * Get the mobile OS
 * @returns {string|boolean} Returns as IOS or ANDROID if mobile. Else, returns false.
 */
export const getMobileOS = (): string | false => {
    if (!isBrowser()) {
        return false;
    }
    const windowObj = window as any;
    const userAgent = windowObj.navigator.userAgent || windowObj.navigator.vendor || windowObj.opera;

    if (/android/i.test(userAgent)) {
        return MOBILE.ANDROID;
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !windowObj.MSStream) {
        return MOBILE.IOS;
    }
    return false;
};

export const getBrowser = (): string => {
    if (!isBrowser()) {
        return BROWSER_TYPES.UNKNOWN;
    }
    const windowObj = window as any;
    if (
        (typeof windowObj['opr'] !== 'undefined' && !!windowObj['opr']['addons']) ||
        !!windowObj['opera'] ||
        navigator.userAgent.indexOf(' OPR/') >= 0
    )
        return BROWSER_TYPES.OPERA;
    if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) return BROWSER_TYPES.FIREFOX;
    if (/^((?!chrome|android|crios).)*safari/i.test(navigator.userAgent)) return BROWSER_TYPES.SAFARI;
    if ((typeof windowObj['isIE'] === 'undefined' && !!windowObj.StyleMedia) || /(edg\/)/i.test(navigator.userAgent))
        return BROWSER_TYPES.EDGE;
    if (typeof (navigator as any).brave !== 'undefined') return BROWSER_TYPES.BRAVE;
    if (/(chrome|crios)/i.test(navigator.userAgent)) return BROWSER_TYPES.CHROME;
    return BROWSER_TYPES.UNKNOWN;
};

export const isBrowser = () => {
    return typeof document !== 'undefined' && typeof window !== 'undefined';
};

export const redirect = (url: string): void => {
    if (!isBrowser()) {
        return;
    }
    window.location.href = url;
};

export const getCurrentDomain = (level = 1, prepend = ''): string => {
    if (!isBrowser()) {
        return '';
    }
    const domainParts = window.location.hostname.split('.');
    level = level > domainParts.length ? domainParts.length : level;
    const returnDomain = [];
    for (let i = level; i >= 0; i--) {
        returnDomain.push(domainParts.pop());
    }
    return prepend + returnDomain.reverse().join('.');
};

/**
 * Get the value of hash part from current URL.
 * @returns {string} The value of hash part
 */
export const getHashPath = () => {
    if (!isBrowser()) {
        return '';
    }
    return window.location.hash.slice(1);
};

/**
Retrieves the value of the hash part from the current URL.
@returns {string} The value of the hash part.
 */
export const getHashPathOnly = () => {
    if (!isBrowser()) {
        return '';
    }
    const hash = window.location.hash;
    if (hash.startsWith('#')) {
        const hashPath = hash.includes('?') ? hash.split('?')[0].substring(1) : hash.substring(1);
        return hashPath;
    }
    return '';
};

/**
 Updates the hash part of the current URL while preserving any existing query parameters.
@param {string} newHash - The new hash value to set.
 */
export const updateHash = (newHash: string) => {
    const url = new URL(window.location.href);
    const currentHash = url.hash;

    if (currentHash.includes('?')) {
        const [hashPath, queryParams] = currentHash.substring(1).split('?');
        url.hash = `${newHash}?${queryParams}`;
    } else {
        url.hash = newHash;
    }

    window.history.replaceState(null, '', url);
};
/**
 * Get current URL's query paramerer.
 * @param {string} name  The name of the query paramerer
 * @returns {string} The requested parameter's value
 */
export const getUrlParam = (name: string): string | null => {
    if (!isBrowser()) {
        return null;
    }
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
};

/**
 * Get current URL's query paramerer , Use in case URL have hash parameters.
 * @param {string} name  The name of the query paramerer
 * @returns {string} The requested parameter's value
 */
export const getHashUrlParam = (name: string): string | null => {
    if (!isBrowser()) {
        return null;
    }
    const urlAfterHash = window.location.hash;
    if (!urlAfterHash) {
        return null;
    }
    const searchParams = new URLSearchParams(urlAfterHash.split('?')[1]);
    if (!searchParams) {
        return null;
    }
    return searchParams.get(name);
};

export const getAllParams = (remove = []): string => {
    if (!isBrowser()) {
        return '';
    }
    if (remove.length) {
        const search = new URLSearchParams(window.location.search);
        for (const key of remove) {
            search.delete(key);
        }
        const paramPart = search.toString();
        return paramPart ? `?${paramPart}` : '';
    }
    return window.location.search;
};

/**
 * Adding event listener to browser history change.
 * @param callback
 * @returns {void} Nothing
 */
export const addListenerToHistoryChange = (callback: any) => {
    if (!isBrowser()) {
        return callback(null);
    }
    window.addEventListener('popstate', callback);
};

/**
 * Change the hash of curernt URL and push the change to browser history.
 * @param route {string} The route
 * @param pageTitle Optional string.
 * @returns {void} Nothing
 */
export const changeHashRoute = (route: string, pageTitle = ''): void => {
    if (!isBrowser()) {
        return;
    }
    window.history.pushState(null, pageTitle, `#${route}`);
};

/**
 * Clean up the current URL from query parameters and hash without reloading the page.
 * @param {string[]} queryRemoval The query parameters that need to be removed.
 * @returns {void}
 */
export const cleanupUrlWithoutReload = (queryRemoval: string[] = []): void => {
    if (!isBrowser()) {
        return;
    }
    let queryPart = '';
    if (queryRemoval.length) {
        const params = new URLSearchParams(window.location.search);
        for (const queryParam of params.keys()) {
            if (queryRemoval.includes(queryParam)) {
                params.delete(queryParam);
            }
        }
        const filteredQuery = params.toString();
        queryPart = filteredQuery ? '?' + filteredQuery : '';
    }
    window.history.replaceState(null, '', window.location.pathname + queryPart);
};

/**
 * Removes specific query parameters from the current URL without reloading the page.
 * Keeps other query parameters and the hash (#) part intact.
 * @param {string[]} paramsToRemove - The query parameters to remove.
 * @returns {void}
 */
export const removeSelectedQueryParams = (paramsToRemove: string[] = []): void => {
    if (typeof window === 'undefined') return;

    const { origin, pathname, search, hash } = window.location;
    let newUrl = `${origin}${pathname}`;
    let updated = false;

    // Case 1: If hash contains query params (#route?foo=1)
    if (hash && hash.includes('?')) {
        const [hashRoute, hashQueryString] = hash.split('?');
        const hashParams = new URLSearchParams(hashQueryString);

        for (const key of paramsToRemove) {
            if (hashParams.has(key)) {
                hashParams.delete(key);
                updated = true;
            }
        }

        const newHashQuery = hashParams.toString();
        newUrl += `${newHashQuery ? `${hashRoute}?${newHashQuery}` : hashRoute}`;
    }

    // Case 2: Otherwise, handle normal query params (?foo=1)
    else {
        const searchParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

        for (const key of paramsToRemove) {
            if (searchParams.has(key)) {
                searchParams.delete(key);
                updated = true;
            }
        }

        const newQuery = searchParams.toString();
        newUrl += newQuery ? `?${newQuery}` : '';
        newUrl += hash || '';
    }

    // Update the URL only if something was actually removed
    if (updated) {
        window.history.replaceState(null, '', newUrl);
    }
};

export const getBrowserVersion = () => {
    const data = [
            { value: 'CriOS', version: 'CriOS' },
            { value: 'Edg', version: 'Edg' },
            { value: 'Firefox', version: 'Firefox' },
            { value: 'Chrome', version: 'Chrome' },
            { value: 'Safari', version: 'Version' },
            { value: 'MSIE', version: 'MSIE' },
            { value: 'Opera', version: 'Opera' },
            { value: 'CLDC', version: 'CLDC' },
            { value: 'Mozilla', version: 'Mozilla' },
        ],
        string = [navigator.userAgent, navigator.platform, navigator.appVersion, navigator.vendor].join(' ');

    for (let i = 0; i < data.length; i++) {
        const regex = new RegExp(data[i].value, 'i');
        const match = regex.test(string);
        if (match) {
            const regexVersion = new RegExp(data[i].version + '[- /:;]([\\d._]+)', 'i');
            const matches = string.match(regexVersion);
            let version = '';
            let matched = '';
            if (matches) {
                if (matches[1]) {
                    matched = matches[1];
                } else {
                    matched = matches[0];
                }
                const matchedArray = matched.split(/[._]+/);
                for (let j = 0; j < matchedArray.length; j += 1) {
                    if (j === 0) {
                        version += matchedArray[j] + '.';
                    } else {
                        version += matchedArray[j];
                    }
                }
            } else {
                version = '0';
            }
            return parseFloat(version);
        }
    }
    return 0;
};

export const getFullPath = (): string => {
    if (!isBrowser()) {
        return '';
    }

    return window.location.pathname;
};

export const getLocalizedValue = (tPaySelectedSubscription: any, language: string, key: string) => {
    // Check if localizedMessages exists and is an object
    if (tPaySelectedSubscription.localizedMessages && typeof tPaySelectedSubscription.localizedMessages === 'object') {
        // Check if the specified language exists and is an array
        if (Array.isArray(tPaySelectedSubscription.localizedMessages[language])) {
            // Find the object with the matching key
            const localizedItem = tPaySelectedSubscription.localizedMessages[language].find(
                (item: any) => item.key === key
            );
            // Return the value if found, otherwise return a default message
            return localizedItem ? localizedItem.value : `${key} not found in ${language}`;
        } else {
            return `Language ${language} not found or is not correctly formatted`;
        }
    } else {
        return key;
    }
};

export const getLowestPrice = (tPayPaymentMethodsList: any, operator: string, language: string, key: string) => {
    // Find the item with the specified operator in tPayPaymentMethodsList
    const selectedItem = tPayPaymentMethodsList.find((item: any) => item.value === operator);

    if (selectedItem && Array.isArray(selectedItem.subscriptions)) {
        // Find the subscription with the lowest price
        const lowestPriceSubscription = selectedItem.subscriptions.reduce((lowest: any, current: any) => {
            return parseFloat(current.price) < parseFloat(lowest.price) ? current : lowest;
        }, selectedItem.subscriptions[0]);

        // Check if localizedMessages exists in the selected subscription and is an object
        if (
            lowestPriceSubscription.localizedMessages &&
            typeof lowestPriceSubscription.localizedMessages === 'object'
        ) {
            // Check if the specified language exists and is an array
            if (Array.isArray(lowestPriceSubscription.localizedMessages[language])) {
                // Find the object with the matching key
                const localizedItem = lowestPriceSubscription.localizedMessages[language].find(
                    (item: any) => item.key === key
                );
                // Return the value if found, otherwise return a default message
                return localizedItem ? localizedItem.value : `${key} not found in ${language}`;
            } else {
                return `Language ${language} not found or is not correctly formatted`;
            }
        } else {
            return `Localized messages not found`;
        }
    } else {
        return `Operator ${operator} not found or has no subscriptions`;
    }
};

/**
 * Removes all query parameters from a given URL or path string.
 * Keeps hash if present.
 * @param {string} url - The full or partial URL.
 * @returns {string} - The cleaned URL without query params.
 */
export const stripAllQueryParamsFromString = (url: string): string => {
    if (!url) return url;

    const [pathAndBeforeHash, hashPart] = url.split('#');
    const cleanPath = pathAndBeforeHash.split('?')[0];
    return hashPart ? `${cleanPath}#${hashPart}` : cleanPath;
};
